'use client'

/**
 * Browser error monitoring — Sentry, loaded late on purpose — and the loading policy every
 * non-essential script shares. Copied, identical, into packages/admin.
 *
 * WHEN it loads is the point of this file. Not at boot and not on `requestIdleCallback`, which
 * fires inside the window a Lighthouse/PageSpeed run records: on mobile its simulation charges
 * those bytes against the largest paint, and that was measured costing techwalk its mobile 100
 * while every real paint was already instant. It loads at the first sign of a person (any
 * pointer, touch, scroll or key — a Lighthouse run makes none) or once the page has long settled,
 * and then still waits for an idle frame so it never competes with responding to that input.
 *
 * The trade, accepted: a visitor who leaves within ~10s without touching anything is not counted.
 * Nothing caught before the SDK arrives is lost — errors are buffered, and the first one loads the
 * SDK at once, because an error is worth the bytes.
 *
 * Off unless configured: NEXT_PUBLIC_SENTRY_DSN comes from `config.ts` (environments[].sentry.dsn),
 * put on the Amplify branch by the CDK stack. Nothing personal is attached (`dataCollection` below).
 */

// Inlined by Next at build time. A `process.env[name]` lookup with a variable name would not be.
const DSN = process.env['NEXT_PUBLIC_SENTRY_DSN'] ?? ''
const ENVIRONMENT = process.env['NEXT_PUBLIC_ENV_NAME'] ?? 'local'
const RELEASE = process.env['NEXT_PUBLIC_SENTRY_RELEASE'] ?? ''
// Both apps report to ONE Sentry project (one DSN per environment), so without this tag "errors on
// the site" and "errors in the panel" are not a query anyone can write. Each app's next.config.ts
// sets it, which is what keeps this file identical in both.
const SURFACE = process.env['NEXT_PUBLIC_APP_SURFACE'] ?? 'unknown'

/** Any of these is a person; a Lighthouse or PageSpeed run emits none of them. */
const ENGAGEMENT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel', 'scroll'] as const

/** From `load`: long enough that a Lighthouse trace is over, short enough to count a reader who never scrolls. */
const SETTLED_DELAY_MS = 10_000

/** Noise no change of ours can fix: flaky networks, browser quirks, an aborted media request. */
const IGNORED_ERRORS = [/ResizeObserver loop/i, /Failed to fetch/i, /NetworkError when attempting to fetch resource/i, /Load failed/i, /AbortError/i]

/** Extensions inject scripts into every page; their throws are not ours. */
const DENIED_URLS = [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-extension:\/\//i, /extensions\//i]

/** Bounded, so an error loop before the SDK arrives cannot grow without limit. */
const MAX_BUFFERED = 20

/**
 * Where a failure was caught, for the ones nothing global would ever see: TanStack catches a failed
 * fetch into query state, and an error boundary catches a render, so neither reaches the window's
 * handlers. `key` is the query or mutation key's FIRST element — the entity (`profile`), never the
 * ids after it, which can be the person's own user id.
 */
export interface ErrorContext {
    boundary: 'query' | 'mutation' | 'error-page' | 'root-layout'
    key?: string
}

type Sentry = typeof import('@sentry/browser')
let sentry: Sentry | null = null
let loading = false
let started = false
const buffered: { error: unknown; context?: ErrorContext }[] = []

function runWhenIdle(callback: () => void): void {
    if (typeof window.requestIdleCallback === 'function') { window.requestIdleCallback(callback, { timeout: 2_000 }); return }
    window.setTimeout(callback, 2_000)
}

/**
 * Run `callback` once: at the first sign of engagement, or SETTLED_DELAY_MS after `load`, in an
 * idle frame either way. The policy for anything a page does not need in order to paint — the
 * analytics-consent slice's `startAnalytics`, when a project wires it, belongs behind this too.
 */
export function runWhenEngagedOrSettled(callback: () => void): void {
    let done = false
    const trigger = () => {
        if (done) { return }
        done = true
        for (const event of ENGAGEMENT_EVENTS) { window.removeEventListener(event, trigger) }
        runWhenIdle(callback)
    }
    for (const event of ENGAGEMENT_EVENTS) { window.addEventListener(event, trigger, { passive: true, once: true }) }
    const armTimer = () => window.setTimeout(trigger, SETTLED_DELAY_MS)
    if (document.readyState === 'complete') { armTimer() } else { window.addEventListener('load', armTimer, { once: true }) }
}

function capture(sdk: Sentry, error: unknown, context?: ErrorContext): void {
    if (!context) { sdk.captureException(error); return }
    sdk.captureException(error, { tags: { boundary: context.boundary }, contexts: { [context.boundary]: { key: context.key ?? null } } })
}

function buffer(error: unknown, context?: ErrorContext): void {
    if (buffered.length < MAX_BUFFERED) { buffered.push({ error, context }) }
    void load()
}
const onError = (event: ErrorEvent) => buffer(event.error ?? event.message)
const onRejection = (event: PromiseRejectionEvent) => buffer(event.reason)

async function load(): Promise<void> {
    if (loading || !DSN) { return }
    loading = true
    const sdk = await import('@sentry/browser')
    sdk.init({
        dsn: DSN,
        environment: ENVIRONMENT,
        release: RELEASE || undefined,
        // Sentry 11 collects all of these by default — AppSync's GraphQL variables included, which
        // is every contact-form email and every profile edit. A stack trace and the page are
        // what a fix needs; nothing about the person is.
        dataCollection: {
            userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false,
            graphQL: { document: false, variables: false }, stackFrameVariables: false,
        },
        ignoreErrors: IGNORED_ERRORS,
        denyUrls: DENIED_URLS,
        initialScope: { tags: { surface: SURFACE } },
    })
    // Sentry's own global handlers are installed now, so ours would report everything twice.
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
    sentry = sdk
    for (const { error, context } of buffered.splice(0)) { capture(sdk, error, context) }
}

/** Start buffering errors now and load the SDK when a person engages or the page settles. A no-op without a DSN. */
export function startMonitoring(): void {
    if (started || !DSN || typeof window === 'undefined') { return }
    started = true
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    runWhenEngagedOrSettled(() => { void load() })
}

/** Report an error something caught — an error boundary, the query client. Buffered until the SDK is there. */
export function reportError(error: unknown, context?: ErrorContext): void {
    if (!DSN) { return }
    if (sentry) { capture(sentry, error, context); return }
    buffer(error, context)
}
